import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import {InductCameraDeviceOverviewContext} from "../context/InductCameraDeviceOverviewContext";
import Alert from "@amzn/meridian/alert"
import {
    isValidEntry,
    IP_ADDRESS_PATTERN,
    PATTERNS, validateNewAsmlComponentDevice, MAC_MASK, PROTOCOLS, VENDOR_INFO_KEYS,
    VENDOR_DISPLAY_VALUES, SPEC_VERSION_DISPLAY_VALUES,
} from "../../../util/Validations";
import {assetTypeToConfigTypeDict, getSortedListWithOther} from "../../../util/Util";
import Select, {SelectOption} from "@amzn/meridian/select";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../../../util/Localization";

const AddInductCameraSingleDeviceModal = (props) => {

    const {formatMessage} = props.intl;
    const {open, setOpen, selected, setSelected} = props;
    const {viewModel, dispatch} = useContext(InductCameraDeviceOverviewContext);
    const {showWarning} = useContext(GlobalStateContext);

    // Visual controls
    const [showAlert, setShowAlert] = useState(false);

    // States
    const [port, setPort] = useState("");
    const [deviceType, setDeviceType] = useState("");
    const [name, setName] = useState("");
    const [ip, setIp] = useState("");
    const [mac, setMac] = useState("");
    const [id, setId] = useState("");
    const [vendor, setVendor] = useState("");
    const [specVersion, setSpecVersion] = useState("");
    const [parentAssetLabel, setParentAssetLabel] = useState("")

    // Error states
    const [portErr, setPortErr] = useState(false);
    const [deviceTypeErr, setDeviceTypeErr] = useState(false);
    const [nameErr, setNameErr] = useState(false);
    const [ipErr, setIpErr] = useState(false);
    const [macErr, setMacErr] = useState(false);
    const [idErr, setIdErr] = useState(false);
    const [vendorErr, setVendorErr] = useState(false);
    const [specVersionErr, setSpecVersionErr] = useState(false);
    const initialMonitoringValue = {
        startTime: "0",
        endTime: "0",
        reason: ""
    }
    const [monitoring, setMonitoring] = useState(JSON.stringify(initialMonitoringValue));

    let stateValues = {
        mac: mac,
        ip: ip,
        name: name,
        deviceType: deviceType,
        port: port,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeASML,
            id: id
        },
        vendorAttributes: {
            vendor: vendor,
            spec_version: specVersion
        },
        monitoring: monitoring,
        parentAssetLabel: parentAssetLabel
    }

    const onCloseModal = () => {
        setOpen(false)
        setSelected("")
        resetInputState();
        setShowAlert(false);
    }

    const onClickConfirm = (stateValues) => {
        if (validateNewAsmlComponentDevice(stateValues)) {
            showWarning(formatMessage({id: "device.editWarning"}), () => {
                setShowAlert(false);
                stateValues.deviceConfig = JSON.stringify(stateValues.deviceConfig)
                if (selected) {
                    stateValues.divertConfig = JSON.stringify(viewModel.asmlComponentInformation[stateValues.mac].divertConfig);
                    updateData(stateValues);
                } else {
                    addData(stateValues);
                }
                setOpen(false);
                resetInputState();
            });
        } else {
            setShowAlert(true);
        }
    };

    function resetInputState() {
        setMac("");
        setMacErr(false);
        setMonitoring(JSON.stringify(initialMonitoringValue));
        setVendor("");
        setSpecVersion("");
        resetAssetDetails();
        clearAssetDetailErrors();
    }

    function resetAssetDetails() {
        setIp("");
        setId("");
        setPort("");
        setDeviceType("");
        setVendorErr(false);
        setSpecVersionErr(false);
        setName("");
    }

    function clearAssetDetailErrors() {
        setIpErr(false);
        setIdErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setNameErr(false);
    }

    const prefill = (value) => {
        if (viewModel.asmlComponentInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.asmlComponentInformation[value];
            const vendorAttributes = device.vendorAttributes;
            clearAssetDetailErrors();
            setMac(device.mac)
            setIp(device.ip);
            setId(device.id || "");
            setPort(device.port);
            setDeviceType(device.deviceType);
            setName(device.name);
            setVendor(vendorAttributes.vendor);
            setSpecVersion(vendorAttributes.spec_version);
            setParentAssetLabel(device.parentAssetLabel);
            const deviceMonitoring = {
                startTime: device.startTime,
                endTime: device.endTime,
                reason: device.reason
            }
            setMonitoring(JSON.stringify(deviceMonitoring));
        } else if (selected) {
            setSelected(false);
            resetAssetDetails()
        }
    }

    const resetDeviceConfigFields = () => {
        setId("");
    }

    const addData = (stateValues) => {
        dispatch({type: 'ADD_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }
    const updateData = (stateValues) => {
        dispatch({type: 'UPDATE_DEVICE_DATA', data: {dispatcher: dispatch, stateValues: stateValues}})
    }

    return (
        <Modal
            title={formatMessage({id: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing={"medium"} spacingInset={"medium"}>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="mac-input">
                        <FormattedMessage id="modal.mac" defaultMessage="MAC Address"/>
                    </Text>
                    <Input id="mac-input"
                           value={mac}
                           onChange={setMac}
                           mask={MAC_MASK}
                           disabled={selected}
                           onBlur={() => {
                               setMacErr(!isValidEntry(stateValues.mac, PATTERNS.MAC));
                               prefill(mac);
                           }}
                           error={macErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.ip" defaultMessage="IP Address"/>
                    </Text>
                    <Input id="ip-input"
                           value={ip}
                           onChange={setIp}
                           onBlur={() => setIpErr(!isValidEntry(stateValues.ip, IP_ADDRESS_PATTERN))}
                           error={ipErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="port-input">
                        <FormattedMessage id="modal.port" defaultMessage="Port"/>
                    </Text>
                    <Input id="port-input"
                           value={port}
                           onChange={setPort}
                           onBlur={() => setPortErr(!isValidEntry(stateValues.port, PATTERNS.PORT))}
                           error={portErr}
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="name-input">
                        <FormattedMessage id="modal.name"
                                          defaultMessage="Device Name"/>
                    </Text>
                    <Input id="name-input"
                           value={name}
                           onChange={setName}
                           onBlur={() => setNameErr(!isValidEntry(stateValues.name, PATTERNS.NAME))}
                           error={nameErr}
                           helperText={<FormattedMessage id="modal.name_line_desc"
                                                         defaultMessage={"Display name of the device. e.g. \"Line 3\""}/> }
                    />
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        <FormattedMessage id="modal.protocol" defaultMessage="Protocol"/>
                    </Text>
                    <Select
                        value={deviceType}
                        onChange={(value) => {
                            setDeviceType(value);
                            resetDeviceConfigFields();
                        }}
                        onBlur={() => setDeviceTypeErr(!deviceType)}
                        placeholder={<FormattedMessage id="modal.select_protocol" defaultMessage="Select a protocol"/>}
                        error={deviceTypeErr}
                        width="100%">
                        {
                            PROTOCOLS.ASML.map(protocol => <SelectOption key={protocol} value={protocol} label={protocol}/>)
                        }
                    </Select>
                </Column>

                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="id-input">
                        <FormattedMessage id="modal.device_id" defaultMessage="Device ID"/>
                    </Text>
                    <Input id="id-input"
                           value={id}
                           onChange={setId}
                           onBlur={() => setIdErr(!isValidEntry(stateValues.deviceConfig.id, PATTERNS.DEVICE_ID))}
                           error={idErr}
                           helperText={<FormattedMessage id="modal.device_id_line_desc"
                                                         defaultMessage={"Unique local identifier for the device. eg. \"3\" for \"Line 3\""}/>}
                    />
                </Column>

                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.vendor'})}
                    </Text>
                    <Select id="device-type-input"
                            value={vendor}
                            onChange={(value) => setVendor(value)}
                            onBlur={() => setVendorErr(!vendor)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_vendor'})}
                            error={vendorErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.VENDOR).map(value => <SelectOption key={value} value={value} label={VENDOR_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary" htmlFor="device-type-input">
                        {getLocalizedMessage({intl: props.intl, stringId: 'modal.spec_version'})}
                    </Text>
                    <Select id="device-type-input"
                            value={specVersion}
                            onChange={(value) => setSpecVersion(value)}
                            onBlur={() => setSpecVersionErr(!specVersion)}
                            placeholder={getLocalizedMessage({intl: props.intl, stringId: 'modal.select_version'})}
                            error={specVersionErr}
                            width="100%">
                        { getSortedListWithOther(VENDOR_INFO_KEYS.SPEC_VERSION).map(value => <SelectOption key={value} value={value} label={SPEC_VERSION_DISPLAY_VALUES[value]}/>) }
                    </Select>
                </Column>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                        <FormattedMessage id="alert.missingfields"
                                          defaultMessage="Please enter all fields in correct format!"/>
                    </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                    </Button>
                    <Button type="primary" size="medium"
                            onClick={() => onClickConfirm(stateValues)}>
                        {selected ? <FormattedMessage id="modal.confirmEdit" defaultMessage="Confirm edit"/> :
                            <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default injectIntl(AddInductCameraSingleDeviceModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asmldevice/modal/AddInductCameraSingleDeviceModal.js